import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { onUnauthorized } from '@/lib/apiClient';
import { login as loginApi, logout as logoutApi, me } from '@/lib/authApi';
import type { AdminProfile } from '@/types/api';
import { ApiError } from '@/types/api';

type AuthStatus = 'booting' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  status: AuthStatus;
  admin: AdminProfile | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('booting');
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const refreshTimer = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await me({ silent: true });
      setAdmin(data.admin);
      setStatus('authenticated');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'unauthorized') {
        setAdmin(null);
        setStatus('anonymous');
        return;
      }
      setStatus((s) => (s === 'booting' ? 'anonymous' : s));
    }
  }, []);

  useEffect(() => {
    refresh();

    const onFocus = () => {
      if (status === 'authenticated') refresh();
    };
    window.addEventListener('focus', onFocus);

    refreshTimer.current = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, REFRESH_INTERVAL_MS);

    const off = onUnauthorized(() => {
      setAdmin(null);
      setStatus('anonymous');
    });

    return () => {
      window.removeEventListener('focus', onFocus);
      if (refreshTimer.current) window.clearInterval(refreshTimer.current);
      off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = useCallback(async (email: string, password: string) => {
    const data = await loginApi(email, password);
    setAdmin(data.admin);
    setStatus('authenticated');
  }, []);

  const handleLogout = useCallback(async () => {
    await logoutApi();
    setAdmin(null);
    setStatus('anonymous');
  }, []);

  return (
    <AuthContext.Provider value={{ status, admin, login: handleLogin, logout: handleLogout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
